/**
 * MiniCalendar Component
 *
 * Displays a compact calendar visualization showing:
 * - Month labels (when weeks span months)
 * - Day headers (M T W T F S S or S M T W T F S)
 * - Calendar days with different types (PTO, holiday, weekend, workday, empty)
 * - Legend explaining day types
 *
 * The calendar respects week start preference (Sunday or Monday) and
 * displays working days as white columns.
 */

import React, { useMemo } from 'react'
import { MiniCalendarDay } from './types'
import { groupIntoWeeks, getMonthLabels } from './utils'
import { DayHeaders } from './components/day-headers'
import { MonthLabels } from './components/month-labels'
import { CalendarDayCell } from './components/calendar-day-cell'
import { CalendarLegend } from './components/calendar-legend'
import { useAppSelector } from '../../../../../store/hooks'
import { selectLocaleSlice } from '../../../../../store/locale-slice'
import '../mini-calendar.less'

export interface MiniCalendarProps {
  /** Array of calendar days to display */
  days: MiniCalendarDay[]
  /** Week start preference: 0 = Sunday, 1 = Monday */
  weekStartsOn?: 0 | 1
  /** Optional CSS class name */
  className?: string
}

/**
 * MiniCalendar displays a compact calendar grid with day types and legend
 *
 * @example
 * <MiniCalendar
 *   days={calendarDays}
 *   weekStartsOn={1}
 * />
 */
export const MiniCalendar: React.FC<MiniCalendarProps> = ({ days, weekStartsOn = 1, className }) => {
  const { locale } = useAppSelector(selectLocaleSlice)

  // Group days into weeks respecting weekStartsOn
  const weeks = useMemo(() => groupIntoWeeks(days, weekStartsOn), [days, weekStartsOn])

  // Get month labels for the first week
  const monthLabels = useMemo(() => getMonthLabels(weeks, locale.locale), [weeks, locale.locale])

  // Get unique day types for legend
  const usedTypes = useMemo(() => {
    return [...new Set(days.map((d) => d.type))] as MiniCalendarDay['type'][]
  }, [days])

  return (
    <div className={`mini-calendar ${className || ''}`}>
      <div className="mini-calendar-content">
        <MonthLabels labels={monthLabels} />

        <DayHeaders weekStartsOn={weekStartsOn} />

        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="mini-calendar-week">
            {week.map((day, dayIndex) => (
              <CalendarDayCell key={dayIndex} day={day} />
            ))}
          </div>
        ))}
      </div>

      <CalendarLegend types={usedTypes} />
    </div>
  )
}
